import * as React from "react";
import { useState, useEffect } from "react";
import { Dialog } from "@/components/ui";
import { useSocialCoachSession } from "@/utils";
import { SmsSetupContext, Step } from "./context";
import { Stepper } from "./components/Stepper";
import { VerifyIdentity } from "./components/VerifyIdentity";
import { EnterPhone } from "./components/EnterPhone";
import { VerifyPhone } from "./components/VerifyPhone";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const SmsSetupDialog: React.FC<Props> = ({ open, onOpenChange, onSuccess }) => {
  const { session } = useSocialCoachSession();

  // If they aren't mfaVerified, we MUST verify their identity (Email/Passkey) before allowing phone setup.
  const initialStep = !session?.mfaVerified ? "VERIFY_IDENTITY" : "ENTER_PHONE";
  const [step, setStep] = useState<Step>(initialStep);
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Sync state when dialog opens
  useEffect(() => {
    if (open) {
      setStep(!session?.mfaVerified ? "VERIFY_IDENTITY" : "ENTER_PHONE");
      setCode("");
      setPhone("");
      setIsLoading(false);
    }
  }, [open]);

  const [code, setCode] = useState(""); // Kept only to clean up in effect

  const contextValue = {
    step,
    setStep,
    phone,
    setPhone,
    isLoading,
    setIsLoading,
    onOpenChange,
    onSuccess,
  };

  return (
    <SmsSetupContext.Provider value={contextValue}>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        size="xl"
        className="h-[min(800px,calc(100dvh-2rem))] max-h-[min(800px,calc(100dvh-2rem))] flex flex-col"
      >
        <Dialog.Content className="flex-1 flex flex-col p-8 overflow-hidden bg-white rounded-2xl shadow-2xl border border-slate-200/50">
          {/* Visual Stepper */}
          <Stepper />

          {/* Separator */}
          <div className="w-full border-b border-slate-100 my-4 shrink-0" />

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto px-4">
            {step === "VERIFY_IDENTITY" && <VerifyIdentity />}
            {step === "ENTER_PHONE" && <EnterPhone />}
            {step === "VERIFY_PHONE" && <VerifyPhone />}
          </div>
        </Dialog.Content>
      </Dialog>
    </SmsSetupContext.Provider>
  );
};
